import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { FaPlus, FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { useToast } from '../../../components/ui/ToastProvider';
import { YButton } from '../../../ui/YButton';
import { YInput } from '../../../ui/YInput';
import { YFormField } from '../../../ui/YFormField';
import { YSheet } from '../../../ui/sheet/YSheet';
import { ClubCard } from './ClubCard';
import { ClubAnnouncementDialog } from './ClubAnnouncementDialog';
import { JoinByCodeWidget } from './JoinByCodeWidget';
import {
  computeClubsSummary,
  filterClubsBySearch,
  formatClubsRegisteredKicker,
} from '../clubsDisplayUtils';
import { fetchClubs, getCachedClubs, setCachedClub, setCachedClubs } from '../utils/clubsCache';
import { COMMUNITY_FIND_CLUB_PATH } from '../../community/constants/communityRoutes';
import { clubMapDrawBoundaryPath } from '../utils/clubMapTabRoutes';
import {
  clearAppChromeKickerOverride,
  setAppChromeKickerOverride,
} from '../../../components/layout/appChromeKicker';
import './ClubsPage.css';
import './clubsIndexFieldEdition.css';

export function ClubsPage() {
  const navigate = useNavigate();
  const { show } = useToast();
  const [clubs, setClubs] = useState(() => getCachedClubs() || []);
  const [loading, setLoading] = useState(() => !getCachedClubs());
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [announcementClub, setAnnouncementClub] = useState(null);

  const [clubName, setClubName] = useState('');
  const [clubDescription, setClubDescription] = useState('');

  useEffect(() => {
    loadClubs();
  }, []);

  const summary = useMemo(() => computeClubsSummary(clubs), [clubs]);

  useEffect(() => {
    if (loading) return undefined;
    setAppChromeKickerOverride(
      formatClubsRegisteredKicker(summary.clubCount, summary.ownedCount)
    );
    return () => clearAppChromeKickerOverride();
  }, [loading, summary.clubCount, summary.ownedCount]);

  const loadClubs = async (force = false) => {
    try {
      const cached = getCachedClubs();
      if (cached) {
        setClubs(cached);
        setLoading(false);
      }
      if (!force && cached) return;
      if (!cached) setLoading(true);
      const response = await fetchClubs(api, { force: true });
      setClubs(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to load clubs:', err);
      setError('Failed to load clubs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClub = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/clubs/', {
        name: clubName,
        description: clubDescription,
      });
      const newClub = response.data;
      setClubs((prev) => {
        const next = [...prev, newClub];
        setCachedClubs(next);
        return next;
      });
      setCachedClub(newClub);
      setShowCreateModal(false);
      setClubName('');
      setClubDescription('');
      navigate(clubMapDrawBoundaryPath(newClub.id));
    } catch (err) {
      console.error('Failed to create club:', err);
      setError('Failed to create club');
    }
  };

  const handleEnterClub = useCallback((club) => {
    navigate(`/clubs/${club.id}?tab=overview`);
  }, [navigate]);

  const handleViewMap = useCallback((club) => {
    navigate(`/clubs/${club.id}/map`);
  }, [navigate]);

  const handleManageMembers = useCallback((club) => {
    navigate(`/clubs/${club.id}?tab=members`);
  }, [navigate]);

  const handleSettings = useCallback((club) => {
    navigate(`/clubs/${club.id}?tab=admin`);
  }, [navigate]);

  const openAnnouncementDialog = useCallback((club) => {
    setAnnouncementClub(club);
  }, []);

  const handleAnnouncementPosted = useCallback(
    (thread) => {
      const club = announcementClub;
      setAnnouncementClub(null);
      show({
        title: 'Announcement posted',
        body: 'Pinned on the club board for 3 days. Members can read and reply.',
        variant: 'success',
      });
      if (club?.id && thread?.id) {
        navigate(`/clubs/${club.id}/board?boardTag=announcement`);
      }
    },
    [announcementClub, navigate, show]
  );

  const filteredClubs = useMemo(
    () => filterClubsBySearch(clubs, searchQuery),
    [clubs, searchQuery]
  );

  const summaryTiles = useMemo(() => {
    const tiles = [
      {
        key: 'acreage',
        label: 'Total Acreage',
        value:
          summary.totalAcreage != null ? summary.totalAcreage.toLocaleString() : '—',
        unit: summary.totalAcreage != null ? 'AC' : '',
      },
      { key: 'members', label: 'Total Members', value: summary.totalMembers, unit: '' },
      { key: 'stands', label: 'Total Stands', value: summary.totalStands, unit: '' },
      { key: 'clubs', label: 'Your Clubs', value: summary.clubCount, unit: '' },
    ];
    return tiles;
  }, [summary]);

  if (loading) {
    return (
      <div className="clubs-page" data-testid="clubs-page">
        <div className="clubs-page__loading">
          <Spinner animation="border" size="sm" role="status" />
          <span>Loading clubs…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="clubs-page" data-testid="clubs-page">
      <div className="clubs-page__inner">
        <header className="clubs-page__header" data-testid="page-hero">
          <div className="clubs-page__header-main clubs-page__header-main--actions-only">
            <div className="clubs-page__header-actions">
              <YButton
                type="button"
                variant="secondary"
                className="clubs-page__find-btn"
                data-testid="clubs-find-club"
                onClick={() => navigate(COMMUNITY_FIND_CLUB_PATH)}
              >
                ＋ Find a Club
              </YButton>
              <YButton
                type="button"
                className="clubs-page__create-btn"
                onClick={() => setShowCreateModal(true)}
              >
                <FaPlus aria-hidden="true" />
                Create Club
              </YButton>
            </div>
          </div>
        </header>

        {error ? (
          <div className="clubs-page__error" role="alert">
            {error}
            <button
              type="button"
              className="btn btn-link btn-sm ms-2 p-0 align-baseline"
              onClick={() => setError('')}
            >
              Dismiss
            </button>
          </div>
        ) : null}

        {clubs.length > 0 ? (
          <>
            <div className="clubs-page__toolbar">
              <div className="clubs-page__search">
                <FaSearch className="clubs-page__search-icon" aria-hidden="true" />
                <YInput
                  type="search"
                  placeholder="Search clubs…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search clubs"
                />
              </div>
            </div>

            <div className="clubs-page__summary" aria-label="Portfolio summary">
              {summaryTiles.map((tile) => (
                <div key={tile.key} className="clubs-page__summary-tile">
                  <div className="clubs-page__summary-label">{tile.label}</div>
                  <div className="clubs-page__summary-value">
                    {tile.value}
                    {tile.unit ? (
                      <span className="clubs-page__summary-unit">{tile.unit}</span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : null}

        {clubs.length === 0 ? (
          <div className="clubs-page__empty">
            <JoinByCodeWidget className="clubs-page__join-widget" />
            <h2 className="clubs-page__empty-title">No clubs yet</h2>
            <p className="clubs-page__empty-text">
              Join with a president&apos;s code above, or create your own club.
            </p>
            <YButton type="button" onClick={() => setShowCreateModal(true)}>
              Create Club
            </YButton>
          </div>
        ) : filteredClubs.length === 0 ? (
          <div className="clubs-page__empty">
            <h2 className="clubs-page__empty-title">No clubs found</h2>
            <p className="clubs-page__empty-text">Try adjusting your search or create a new club</p>
          </div>
        ) : (
          <>
            <div className="clubs-page__grid">
              {filteredClubs.map((club) => (
                <ClubCard
                  key={club.id}
                  club={club}
                  onEnter={handleEnterClub}
                  onViewMap={handleViewMap}
                  onManageMembers={handleManageMembers}
                  onSettings={handleSettings}
                  onPostAnnouncement={openAnnouncementDialog}
                />
              ))}
            </div>
            <JoinByCodeWidget
              variant="compact"
              className="clubs-page__join-widget clubs-page__join-widget--footer"
              title="Got an invite code?"
              description="Enter a code from a club president to join another club."
            />
          </>
        )}
      </div>

      <YSheet
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        kicker="CREATE"
        title="New Club"
        testId="clubs-create-sheet"
        footer={
          <>
            <YButton type="button" variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </YButton>
            <YButton type="submit" form="clubs-create-form">
              Create Club
            </YButton>
          </>
        }
      >
        <Form id="clubs-create-form" onSubmit={handleCreateClub}>
          <YFormField label="Club Name" htmlFor="club-create-name" labelClassName="y-label y-label--dialog" required>
            <YInput
              id="club-create-name"
              type="text"
              value={clubName}
              onChange={(e) => setClubName(e.target.value)}
              placeholder="Enter club name"
              required
            />
          </YFormField>
          <YFormField
            label="Description"
            htmlFor="club-create-description"
            labelClassName="y-label y-label--dialog"
          >
            <YInput
              id="club-create-description"
              type="textarea"
              rows={3}
              value={clubDescription}
              onChange={(e) => setClubDescription(e.target.value)}
              placeholder="Optional description"
            />
          </YFormField>
        </Form>
      </YSheet>

      <ClubAnnouncementDialog
        show={Boolean(announcementClub)}
        onHide={() => setAnnouncementClub(null)}
        clubId={announcementClub?.id}
        clubName={announcementClub?.name}
        memberCount={announcementClub?.member_count}
        onPosted={handleAnnouncementPosted}
      />
    </div>
  );
}
